<template>
  <div class="copilotKitChat">
    <div class="copilotKitMessages" ref="messages">
      <div class="copilotKitMessagesContainer">

        <!-- Welcome — shown only before any messages. Starters are tagged Action / Help (design);
             what each one does is unchanged. -->
        <div v-if="messages.length === 0" class="agent-welcome" data-test="agent-welcome">
          <span class="agent-welcome-mark" v-html="SparklesIcon"></span>
          <div class="agent-welcome-heading">{{ greeting }}</div>
          <div class="agent-welcome-sub">Ask me anything about your practice — I can automate tasks, look things up, or answer questions.</div>
          <div class="agent-welcome-pills">
            <button
              v-for="starter in starters"
              :key="starter.key"
              type="button"
              class="agent-welcome-pill"
              :data-test="`agent-starter-${starter.key}`"
              @click="onStarter(starter)"
            >
              <span class="agent-welcome-pill-icon" v-html="starter.svg"></span>
              <span class="agent-welcome-pill-label">{{ starter.label }}</span>
              <span class="agent-welcome-pill-kind">{{ starter.kind }}</span>
            </button>
          </div>
        </div>

        <template v-for="(group, gi) in groupedMessages">

          <!-- Collapsible trace block for consecutive steps -->
          <div v-if="group.type === 'steps'" :key="`trace-${gi}`" class="agent-trace-group">
            <button class="agent-trace-header" @click="toggleTrace(gi)">
              <span class="agent-trace-status">
                <span v-if="!group.allDone" class="agent-trace-spinner"></span>
                <span v-else class="agent-trace-done-icon">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <circle cx="6" cy="6" r="5.5" fill="#22c55e" stroke="none"/>
                    <path d="M3.5 6l2 2 3-3" stroke="white" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </span>
              <span class="agent-trace-label">
                <span v-if="!group.allDone">{{ activeStep(group) }}</span>
                <span v-else>{{ group.messages.length }} steps completed</span>
              </span>
              <span v-if="group.allDone" class="agent-trace-chevron">
                {{ traceCollapsed[gi] ? '›' : '⌄' }}
              </span>
            </button>

            <div v-if="!group.allDone || !traceCollapsed[gi]" class="agent-trace-steps">
              <AgentMessage v-for="msg in group.messages" :key="msg.id" :msg="msg" @send="$emit('send', $event)" />
            </div>
          </div>

          <!-- Regular messages -->
          <template v-else>
            <AgentMessage v-for="msg in group.messages" :key="msg.id" :msg="msg" @send="$emit('send', $event)" />
          </template>

        </template>

      </div>

      <div v-if="isTyping" class="copilotKitMessagesFooter">
        <div class="agent-typing-indicator">
          <span class="agent-typing-dot"></span>
          <span class="agent-typing-dot"></span>
          <span class="agent-typing-dot"></span>
        </div>
      </div>
    </div>

    <!-- OTP input bar -->
    <div v-if="otpRequired" class="agent-otp-bar">
      <span class="agent-otp-label">Verification code:</span>
      <input
        v-model="otpCode"
        class="agent-otp-input"
        type="text"
        maxlength="6"
        placeholder="000000"
        @keydown.enter="submitOtp"
      />
      <button class="agent-otp-submit" @click="submitOtp" :disabled="otpCode.length < 4">Submit</button>
    </div>

    <AgentInput
      :is-running="isRunning"
      placeholder="Ask Teamio anything…"
      @send="$emit('send', $event)"
      @stop="$emit('stop')"
    />
  </div>
</template>

<script>
import AgentMessage from './AgentMessage.vue';
import AgentInput from './AgentInput.vue';
import { lucideIcon } from './agentIcons';

// `send` text starters post that text; `call` opens the AI Call modal — same as before the redesign.
const STARTERS = [
  { key: 'insurance', icon: 'shield-check', label: 'Verify insurance for a patient', kind: 'Action', send: 'Verify insurance for a patient' },
  { key: 'call', icon: 'phone-call', label: 'Do a follow-up call', kind: 'Action', openAiCall: true },
  { key: 'reminders', icon: 'calendar', label: 'How do I set up appointment reminders?', kind: 'Help', send: 'How do I set up appointment reminders?' },
  { key: 'help', icon: 'sparkles', label: 'What can you help me with?', kind: 'Help', send: 'What can you help me with?' },
];

export default {
  name: 'AgentChat',
  components: { AgentMessage, AgentInput },
  props: {
    messages: { type: Array, default: () => [] },
    isRunning: { type: Boolean, default: false },
    otpRequired: { type: Boolean, default: false },
    userFirstName: { type: String, default: '' },
  },
  data() {
    return {
      otpCode: '',
      userScrolledUp: false,
      observer: null,
      traceCollapsed: {},
      SparklesIcon: lucideIcon('sparkles', 22),
      starters: STARTERS.map((s) => ({ ...s, svg: lucideIcon(s.icon, 16) })),
    };
  },
  computed: {
    greeting() {
      return this.userFirstName ? `Hi ${this.userFirstName}, how can I help?` : 'Hi, how can I help?';
    },
    isTyping() {
      return this.isRunning && !this.messages.some(m => m.isStreaming);
    },
    groupedMessages() {
      const groups = [];
      for (const msg of this.messages) {
        if (msg.type === 'step') {
          const last = groups[groups.length - 1];
          if (last && last.type === 'steps') {
            last.messages.push(msg);
            last.allDone = last.messages.every(m => m.status === 'done');
          } else {
            groups.push({ type: 'steps', messages: [msg], allDone: msg.status === 'done' });
          }
        } else {
          const last = groups[groups.length - 1];
          if (last && last.type === 'other') {
            last.messages.push(msg);
          } else {
            groups.push({ type: 'other', messages: [msg] });
          }
        }
      }
      return groups;
    },
  },
  watch: {
    groupedMessages(groups) {
      // Auto-collapse trace groups 900ms after all steps complete
      groups.forEach((group, i) => {
        if (group.type === 'steps' && group.allDone && !this.traceCollapsed[i]) {
          setTimeout(() => {
            this.$set(this.traceCollapsed, i, true);
          }, 900);
        }
      });
    },
  },
  mounted() {
    this.observer = new MutationObserver(() => {
      if (!this.userScrolledUp) this.scrollToBottom();
    });
    this.observer.observe(this.$refs.messages, { childList: true, subtree: true });
    this.$refs.messages.addEventListener('scroll', this.onScroll);
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    if (this.$refs.messages) {
      this.$refs.messages.removeEventListener('scroll', this.onScroll);
    }
  },
  methods: {
    scrollToBottom() {
      const el = this.$refs.messages;
      if (el) el.scrollTop = el.scrollHeight;
    },
    onScroll() {
      const el = this.$refs.messages;
      if (!el) return;
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      this.userScrolledUp = distFromBottom > 50;
    },
    activeStep(group) {
      const running = group.messages.find(m => m.status === 'running');
      return running ? running.content : group.messages[group.messages.length - 1].content;
    },
    toggleTrace(gi) {
      const group = this.groupedMessages[gi];
      if (group && group.allDone) {
        this.$set(this.traceCollapsed, gi, !this.traceCollapsed[gi]);
      }
    },
    onStarter(starter) {
      if (starter.openAiCall) this.$emit('open-ai-call');
      else this.$emit('send', starter.send);
    },
    submitOtp() {
      if (this.otpCode.length < 4) return;
      this.$emit('submit-otp', this.otpCode);
      this.otpCode = '';
    },
  },
};
</script>
